import React, { useContext, useMemo } from 'react';
import { EventDatetimeContext } from 'components/EventDatetime';
import { useSelector } from 'react-redux';
import { selectLocale } from 'models/localization';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import { selectLocalizedEventName } from 'models/virtual_event';
import styled from 'styled-components';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import ReactHtmlParser from 'react-html-parser';

import DateText from 'pages/landing/DateText';
import RegisterButton from 'pages/landing/RegisterButton';
import Countdown from 'pages/landing/Countdown';

enum EnabledState {
  SHOW = 'SHOW',
  HIDE = 'HIDE',
  REMOVE = 'REMOVE',
}

const HiddenWrapper = styled.div`
  visibility: hidden;
`;

const HeaderImage = styled.img<{ width: string; maxWidth: string }>`
  width: ${({ width }) => width};
  max-width: ${({ maxWidth }) => maxWidth};
`;

const WelcomeText = styled.div`
  color: #FFF;
  font-family: din, sans-serif;
  font-weight: 500;
  font-size: 30px;
  text-align: center;
  flex-shrink: 0;
  max-width: 100%;

  @media screen and (min-width: 200px) {
    font-size: 28px;
    line-height: 36px;
  }

  @media screen and (min-width: 400px) {
    font-size: 36px;
    line-height: 48px;
  }

  @media screen and (min-width: 600px) {
    font-size: 40px;
    line-height: 60px;
  }

  @media screen and (min-width: 960px) {
    font-size: 48px;
    line-height: 60px;
  }
`;

const RtfText = styled.div`
  color: #FFF;
  font-family: din, sans-serif;
  font-weight: 500;
  width: 100%;
  text-align: center;
  flex-shrink: 0;
  max-width: 90vw;

  @media screen and (min-width: 400px) {
    font-size: 24px;
  }

  @media screen and (min-width: 600px) {
    font-size: 27px;
  }

  @media screen and (min-width: 960px) {
    font-size: 30px;
    max-width: 960px;
  }
`;

const StyledCountdown = styled(Countdown)`
  flex-shrink: 0;
`;

const enabledStateHandler = (state: EnabledState, value: any) => {
  switch (state) {
    case EnabledState.REMOVE: return null;
    case EnabledState.HIDE: return (
      <HiddenWrapper>
        {value}
      </HiddenWrapper>
    );
    default: return value;
  }
};

const useLandingItems = (openResetEmailModal: () => void) => {
  const { t } = useTranslation();
  const { startDatetime, endDatetime } = useContext(EventDatetimeContext);
  const {
    timeZone: globalTimeZone,
    landing: {
      regLink,
      topButtonColor,
      bottomButtonColor,
      timeZone: landingTimeZone,
      dateFormat,
      timeFormat,
      zoneAbbr,
      header,
      richTextField,
      richTextFields,
      enabledComponents,
      buttonTexts,
    },
    themeColor,
  } = useSelector((state: any) => state.eventConfigs);
  const eventName = useSelector(selectLocalizedEventName);
  const timeZone = landingTimeZone || globalTimeZone;
  const locale = useSelector(selectLocale);
  const resolvedTopButtonColor = topButtonColor || themeColor;
  const resolvedBottomButtonColor = bottomButtonColor || resolvedTopButtonColor;

  const filteredComponents = useMemo(() => {
    const components = {
      header: header ? (
        <HeaderImage
          id="landing-header-image"
          src={header.imageSrc}
          width={header.width}
          alt={header.alt}
          maxWidth={header.maxWidth}
        />
      ) : null,
      eventTitle: (
        <WelcomeText id="event-title">
          {eventName}
        </WelcomeText>
      ),
      // TODO: Add Localization
      eventDate: (
        <DateText>
          <span id="event-date">
            {`Date: ${startDatetime.tz(timeZone).format(dateFormat || 'DD MMMM YYYY')}`}
          </span>
        </DateText>
      ),
      eventTime: (
        <DateText>
          <span id="event-time">
            {`Time: ${zoneAbbr || moment().tz(timeZone).zoneAbbr()} ${startDatetime.tz(timeZone).format(timeFormat || 'hh:mm')} - ${endDatetime.tz(timeZone).format(timeFormat || 'hh:mm')}`}
          </span>
        </DateText>
      ),
      countdown: <StyledCountdown id="landing-countdown" />,
      registerButton: (
        <RegisterButton
          href={regLink}
          target="_blank"
          id="landing-register-button"
          rel="noopener noreferrer"
          backgroundColor={resolvedTopButtonColor}
        >
          {findAvailableLocale(buttonTexts?.register, locale) || t('lobby.register', 'Register')}
        </RegisterButton>
      ),
      registeredButton: (
        <RegisterButton
          id="landing-registered-button"
          onClick={openResetEmailModal}
          backgroundColor={resolvedBottomButtonColor}
        >
          {findAvailableLocale(buttonTexts?.registered, locale) || t('lobby.registered', 'I have registered')}
        </RegisterButton>
      ),
      rtfText: <RtfText id="landing-rtf">{ReactHtmlParser(findAvailableLocale(richTextField, locale))}</RtfText>,
      richTextFields: (richTextFields || []).map((sentence, index) => ( //! To be deprecated
        <RtfText key={`landing-rtf-${index}`} id={`landing-rtf-${index}`}>{ReactHtmlParser(sentence)}</RtfText>
      )),
    };
    return Object.entries(components).reduce((result, [key, value]) => {
      result[key] = enabledStateHandler(enabledComponents?.[key], value);
      return result;
    }, {}) as (typeof components);
  }, [locale, t]);

  return filteredComponents;
};

export default useLandingItems;
